import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { InvalidParamError } from '@dynamic-labs-sdk/client';
import {
  type TransferAmountParams,
  getWalletProviderFromWalletAccount,
  parseAmountToSmallestUnit,
} from '@dynamic-labs-sdk/client/core';

import type { AptosTransactionPayload } from '../../AptosWalletProvider.types';
import { NotAptosProviderError } from '../../errors/NotAptosProviderError';
import { isAptosWalletAccount } from '../../isAptosWalletAccount';
import { isAptosWalletProvider } from '../../isAptosWalletProvider';

const APT_DECIMALS = 8;

/**
 * Transfers native APT or fungible tokens from one wallet to another.
 *
 * Converts the human-readable amount string to the smallest unit (octas for APT,
 * or the appropriate unit for fungible tokens) using string-based arithmetic
 * to avoid floating-point precision issues.
 */
export const aptosTransferAmount = async (
  { amount, recipient, token, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isAptosWalletAccount(walletAccount)) {
    throw new InvalidParamError(
      'Wallet account is not an Aptos wallet account'
    );
  }

  const provider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (!isAptosWalletProvider(provider)) {
    throw new NotAptosProviderError({ providerKey: provider.key });
  }

  const decimals = token?.decimals ?? APT_DECIMALS;
  const smallestUnitAmount = parseAmountToSmallestUnit({ amount, decimals });

  let transaction: AptosTransactionPayload;
  if (token?.address) {
    transaction = {
      function: '0x1::primary_fungible_store::transfer',
      functionArguments: [token.address, recipient, Number(smallestUnitAmount)],
      typeArguments: ['0x1::fungible_asset::Metadata'],
    };
  } else {
    transaction = {
      function: '0x1::coin::transfer',
      functionArguments: [recipient, Number(smallestUnitAmount)],
      typeArguments: ['0x1::aptos_coin::AptosCoin'],
    };
  }

  const result = await provider.signAndSubmitTransaction({
    transaction,
    walletAccount,
  });

  return { transactionHash: result.hash };
};
